// src/components/TagItSDKLoader.jsx
"use client";

import Script from "next/script";
import { useEffect, useMemo, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import { usePortal } from "@/context/PortalContext";
import { buildAnalyticsContext } from "@/lib/analyticsContext.mjs";
import { getNativeAdvertisingIdentity } from "@/lib/nativeAdvertisingIdentity";
import { getNativeDeviceMetadata } from "@/lib/nativeDeviceMetadata";
import { buildTagItUserIdentifier } from "@/lib/tagitIdentity.mjs";
import { TAGIT_LOCAL_STORAGE_ID_MIGRATION_SCRIPT } from "@/lib/tagitLocalStorageIdentity.mjs";

/**
 * Loads TagIt SDK (initiator + config) with the native Firebase UID as identifier.
 * Controlled by env:
 * - NEXT_PUBLIC_TAGIT_TID (tagId)
 * - NEXT_PUBLIC_TAGIT_SDK (collector URL)
 * - NEXT_PUBLIC_TAGIT_INITIATOR (optional override; defaults to dev initiator)
 */
export default function TagItSDKLoader() {
  const { user } = useAuth();
  const portal = usePortal();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [nativeIdentity, setNativeIdentity] = useState(null);
  const [nativeDevice, setNativeDevice] = useState(null);
  const [nativeReady, setNativeReady] = useState(false);

  const tagId = process.env.NEXT_PUBLIC_TAGIT_TID || "";
  const sdkURL = process.env.NEXT_PUBLIC_TAGIT_SDK || "";
  const initiatorURL =
    process.env.NEXT_PUBLIC_TAGIT_INITIATOR || "https://storage.googleapis.com/tagitsdk-dev/initiator.js";
  const debugMode = (process.env.NEXT_PUBLIC_TAGIT_DEBUG || "false").toLowerCase() === "true";
  const enableLocalTagIt =
    (process.env.NEXT_PUBLIC_TAGIT_ENABLE_LOCAL || "false").toLowerCase() === "true";
  const disableLocalDevTagIt = process.env.NODE_ENV === "development" && !enableLocalTagIt;
  const enableTagIt = Boolean(tagId && sdkURL) && !disableLocalDevTagIt;
  const ready = nativeReady;
  const eventSubContext = buildAnalyticsContext({
    pathname,
    searchParams,
    surfaceId: portal?.id || "sproutly",
  });

  useEffect(() => {
    let cancelled = false;
    setNativeReady(false);

    Promise.all([getNativeAdvertisingIdentity(), getNativeDeviceMetadata()])
      .then(([identity, device]) => {
        if (!cancelled) {
          setNativeIdentity(identity);
          setNativeDevice(device);
          setNativeReady(true);
        }
      })
      .catch((err) => {
        console.warn("[tagIt] failed to resolve native advertising identity", err);
        if (!cancelled) {
          setNativeIdentity(null);
          setNativeReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const identifiersJson = useMemo(() => {
    const identifier = buildTagItUserIdentifier(user?.uid);
    return JSON.stringify(identifier ? [identifier] : []);
  }, [user?.uid]);

  const nativeEntityFieldsJson = useMemo(() => {
    return JSON.stringify(nativeIdentity || {});
  }, [nativeIdentity]);

  const nativeDeviceJson = useMemo(() => {
    return JSON.stringify(nativeDevice || {});
  }, [nativeDevice]);

  const tagItConfig = useMemo(() => {
    if (!enableTagIt || !ready) return null;
    return {
      tagId,
      sdkURL,
      eventSubContext,
      data: [
        {
          ...(nativeIdentity || {}),
          nativeDevice: nativeDevice || {},
          eventSubContext,
          entityData: { identifiers: JSON.parse(identifiersJson) },
          privacy: {},
        },
      ],
      features: [
        {
          collectDeviceData: true,
          debugMode,
          doNotCollectPurposes: {},
          enableKetch: false,
        },
      ],
    };
  }, [debugMode, enableTagIt, eventSubContext, identifiersJson, nativeDevice, nativeIdentity, ready, sdkURL, tagId]);

  useEffect(() => {
    if (!tagItConfig || typeof window === "undefined") return;
    window.tagItConfig = tagItConfig;
    window.dispatchEvent(new Event("sproutlyTagItConfigReady"));
  }, [tagItConfig]);

  if (!enableTagIt || !ready) return null;

  const hasNativeIdentity = Boolean(nativeIdentity?.entityId);

  return (
    <>
      <Script
        id="tagit-local-storage-identity"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: TAGIT_LOCAL_STORAGE_ID_MIGRATION_SCRIPT,
        }}
      />
      <Script
        id="tagit-config"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.tagItConfig = {
              tagId: "${tagId}",
              sdkURL: "${sdkURL}",
              eventSubContext: ${JSON.stringify(eventSubContext)},
              data: [{
                ...${nativeEntityFieldsJson},
                nativeDevice: ${nativeDeviceJson},
                eventSubContext: ${JSON.stringify(eventSubContext)},
                entityData: { identifiers: ${identifiersJson} },
                privacy: {}
              }],
              features: [{
                collectDeviceData: true,
                debugMode: ${debugMode},
                doNotCollectPurposes: {},
                enableKetch: false
              }]
            };
          `,
        }}
      />
      {!hasNativeIdentity ? (
        <Script id="tagit-initiator" src={initiatorURL} strategy="afterInteractive" async />
      ) : null}
    </>
  );
}
